import { Box, Card, CardContent, Typography, Button, Stack, Dialog, DialogTitle, DialogContent, DialogActions, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import PersonAddAlt1OutlinedIcon from '@mui/icons-material/PersonAddAlt1Outlined';
import AssessmentOutlinedIcon from '@mui/icons-material/AssessmentOutlined';
import LocalHospitalOutlinedIcon from '@mui/icons-material/LocalHospitalOutlined';
import FactCheckOutlinedIcon from '@mui/icons-material/FactCheckOutlined';
import PaidOutlinedIcon from '@mui/icons-material/PaidOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import { CreateUserModal } from "../../components/UserManagement/CreateUserModal";
import { ROUTES } from "../../router/constants";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../redux/store";
import dayjs from "../../lib/dayjs";
import { toast } from "sonner";
import {
  getMySchedulesByDate,
  type DoctorScheduleAppointment,
  type NextAvailabilityLocal,
  getMyLinkedDoctors,
  type DoctorSearchResult,
  upsertDoctorLocal,
} from "../../services/doctors.service";
import { DoctorUpcomingAppointmentCard } from "../../components/Appointments/DoctorUpcomingAppointmentCard";
import { findRecordIdForAppointment } from "../../services/medicalRecords.service";

export function Dashboard() {

  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.userInfo);

  const allowedReportRoles = ["ADMIN", "OWNER", "DOCTOR_OWNER"];
  const allowedFinanceRoles = ["ADMIN", "OWNER", "DOCTOR_OWNER", "FINANCIAL"];
  const userRoleUpper = String(user?.role || "").toUpperCase();
  const canAccessReports = allowedReportRoles.includes(userRoleUpper);
  const canAccessAuditLogs = allowedReportRoles.includes(userRoleUpper);
  const canManageUsers = allowedReportRoles.includes(userRoleUpper);
  const canAccessFinance = allowedFinanceRoles.includes(userRoleUpper);
  const isDoctor = userRoleUpper === "DOCTOR" || userRoleUpper === "DOCTOR_OWNER";
  const isSecretary = userRoleUpper === "SECRETARY";

  type DoctorUpcomingWithLocal = {
    appointment: DoctorScheduleAppointment;
    local: NextAvailabilityLocal | null;
  };

  const [upcomingAppointments, setUpcomingAppointments] = useState<DoctorUpcomingWithLocal[]>([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(false);

  // SECRETARY - médicos vinculados
  const [linkedDoctors, setLinkedDoctors] = useState<DoctorSearchResult[]>([]);
  const [loadingLinkedDoctors, setLoadingLinkedDoctors] = useState(false);
  const [linkedDoctorsError, setLinkedDoctorsError] = useState<string | null>(null);

  // Modal de clínica (locais) por médico vinculado
  type ClinicLocalForm = {
    id?: string;
    localName: string;
    address: string;
    city: string;
    state: string;
    zip: string;
    phone: string;
  };

  const emptyClinicLocal: ClinicLocalForm = {
    localName: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    phone: "",
  };

  const [clinicModalOpen, setClinicModalOpen] = useState(false);
  const [clinicDoctor, setClinicDoctor] = useState<DoctorSearchResult | null>(null);
  const [clinicLocal, setClinicLocal] = useState<ClinicLocalForm>(emptyClinicLocal);
  const [savingClinicLocal, setSavingClinicLocal] = useState(false);

  useEffect(() => {
    if (!isDoctor) {
      setUpcomingAppointments([]);
      return;
    }

    let ignore = false;

    async function load() {
      try {
        setLoadingUpcoming(true);
        const today = dayjs().startOf("day");
        const daysToLookAhead = 3; // hoje + próximos 2 dias

        const collected: DoctorUpcomingWithLocal[] = [];

        for (let offset = 0; offset < daysToLookAhead; offset++) {
          const dateStr = today.add(offset, "day").format("YYYY-MM-DD");
          const schedules = await getMySchedulesByDate(dateStr);

          schedules.forEach((schedule) => {
            (schedule.appointments || []).forEach((appt) => {
              collected.push({ appointment: appt, local: schedule.local });
            });
          });

          if (collected.length >= 3) break;
        }

        const now = dayjs();
        const upcoming = collected
          .filter(({ appointment }) => dayjs(appointment.startTime).isSameOrAfter(now))
          .sort((a, b) => dayjs(a.appointment.startTime).valueOf() - dayjs(b.appointment.startTime).valueOf())
          .slice(0, 3);

        if (!ignore) setUpcomingAppointments(upcoming);
      } catch (e) {
        if (!ignore) {
          setUpcomingAppointments([]);
        }
      } finally {
        if (!ignore) setLoadingUpcoming(false);
      }
    }

    load();

    return () => {
      ignore = true;
    };
  }, [isDoctor]);

  // Carrega médicos vinculados quando usuário for SECRETARY
  useEffect(() => {
    if (!isSecretary) return;

    let ignore = false;
    async function loadLinkedDoctors() {
      try {
        setLoadingLinkedDoctors(true);
        setLinkedDoctorsError(null);
        const list = await getMyLinkedDoctors();
        if (ignore) return;
        setLinkedDoctors(list);
      } catch (e) {
        if (!ignore) {
          console.error(e);
          setLinkedDoctorsError("Não foi possível carregar os médicos vinculados.");
        }
      } finally {
        if (!ignore) setLoadingLinkedDoctors(false);
      }
    }

    loadLinkedDoctors();
    return () => {
      ignore = true;
    };
  }, [isSecretary]);
  
  const handleOpenSchedule = () => {
      navigate(ROUTES.SCHEDULE);
  };

  const handleOpenReports = () => {
      navigate(ROUTES.REPORTS);
  };

  const handleOpenFinance = () => {
      navigate(ROUTES.FINANCE);
  };

  const handleOpenAuditLogs = () => {
      navigate(ROUTES.AUDIT_LOGS);
  };

  const handleOpenClinicModal = (doctor: DoctorSearchResult) => {
    setClinicDoctor(doctor);
    const existing = (doctor.locals && doctor.locals.length > 0) ? doctor.locals[0] : null;
    setClinicLocal({
      id: existing?.id,
      localName: existing?.localName || "",
      address: existing?.address || "",
      city: existing?.city || "",
      state: existing?.state || "",
      zip: existing?.zip_code || "",
      phone: existing?.phone || "",
    });
    setClinicModalOpen(true);
  };

  const handleCloseClinicModal = () => {
    if (savingClinicLocal) return;
    setClinicModalOpen(false);
    setClinicDoctor(null);
    setClinicLocal(emptyClinicLocal);
  };

  const handleSaveClinicLocal = async () => {
    if (!clinicDoctor) return;

    const hasAnyField = [
      clinicLocal.localName,
      clinicLocal.address,
      clinicLocal.city,
      clinicLocal.state,
      clinicLocal.zip,
      clinicLocal.phone,
    ].some((v) => v && v.trim() !== "");

    if (!hasAnyField) {
      // nada preenchido; não faz sentido salvar
      return;
    }

    try {
      setSavingClinicLocal(true);
      const payload = {
        doctorId: clinicDoctor.doctorId,
        localId: clinicLocal.id,
        localName: clinicLocal.localName || undefined,
        address: clinicLocal.address || undefined,
        city: clinicLocal.city || undefined,
        state: clinicLocal.state || undefined,
        zip_code: clinicLocal.zip || undefined,
        phone: clinicLocal.phone || undefined,
      };

      const saved = await upsertDoctorLocal(payload);

      const updatedLocal: NextAvailabilityLocal = {
        id: saved.id,
        localName: saved.localName ?? null,
        address: saved.address ?? null,
        city: saved.city ?? null,
        state: saved.state ?? null,
        zip_code: saved.zip_code ?? null,
        phone: saved.phone ?? null,
      };

      // Atualiza estado local dos médicos vinculados com o dado retornado (mantendo múltiplas clínicas)
      setLinkedDoctors((prev) =>
        prev.map((doc) => {
          if (doc.doctorId !== clinicDoctor.doctorId) return doc;

          const prevLocals = doc.locals ?? [];
          const existingIndex = prevLocals.findIndex((l) => l.id === saved.id);

          const newLocals =
            existingIndex >= 0
              ? prevLocals.map((l, idx) => (idx === existingIndex ? updatedLocal : l))
              : [...prevLocals, updatedLocal];

          return {
            ...doc,
            locals: newLocals,
          };
        }),
      );

      setClinicDoctor((prev) => {
        if (!prev || prev.doctorId !== clinicDoctor.doctorId) return prev;

        const prevLocals = prev.locals ?? [];
        const existingIndex = prevLocals.findIndex((l) => l.id === saved.id);

        const newLocals =
          existingIndex >= 0
            ? prevLocals.map((l, idx) => (idx === existingIndex ? updatedLocal : l))
            : [...prevLocals, updatedLocal];

        return {
          ...prev,
          locals: newLocals,
        };
      });

      handleCloseClinicModal();
    } catch (e) {
      console.error(e);
    } finally {
      setSavingClinicLocal(false);
    }
  };

  const [openCreateUserModal, setOpenCreateUserModal] = useState(false);

  return (
    <Box
      width="100%"
      padding={4}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        minHeight: "100vh",
      }}
    >
      <Box sx={{ width: "100%", maxWidth: 600, mx: "auto", mt: 6, display: "flex", flexDirection: "column", gap: 3 }}>
        <Card>
          <CardContent>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", sm: "center" }}
              spacing={2}
            >
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  {isDoctor ? "Minha agenda" : "Agenda"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {isDoctor
                    ? "Configure seus horários disponíveis para consulta e, se quiser, integre com o Google Calendar."
                    : "Acesse as agendas dos médicos vinculados."}
                </Typography>
              </Box>
              <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOpenSchedule}
                  sx={{
                    width: { xs: "100%", sm: 260 },
                    whiteSpace: "nowrap",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <CalendarMonthOutlinedIcon />
                  Agenda
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {isDoctor && (upcomingAppointments.length > 0 || loadingUpcoming) && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Próximas consultas
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Veja os seus próximos três agendamentos.
              </Typography>

              <Stack spacing={2} sx={{ mt: 2 }}>
                {loadingUpcoming && upcomingAppointments.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Carregando próximos agendamentos...
                  </Typography>
                )}

                {!loadingUpcoming && upcomingAppointments.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    Nenhuma consulta futura encontrada nos próximos dias.
                  </Typography>
                )}

                {upcomingAppointments.map(({ appointment, local }) => (
                  <DoctorUpcomingAppointmentCard
                    key={appointment.id}
                    appointment={appointment}
                    local={local}
                    doctorId={user?.doctor?.doctorId}
                    onOpenRecord={async (appointmentId, patientId, doctorId) => {
                      const existingId = patientId
                        ? await findRecordIdForAppointment(patientId, appointmentId)
                        : null;
                      if (existingId) {
                        navigate(`/prontuario/${existingId}`);
                      } else {
                        navigate(`/prontuario/novo?appointmentId=${appointmentId}&patientId=${patientId}&doctorId=${doctorId}`);
                      }
                    }}
                    onOpenHistory={(patientId) =>
                      navigate(`/paciente/${patientId}/historico`)
                    }
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        )}

        {isDoctor && (
          <Card>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2}
              >
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Prontuários
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Veja todos os prontuários que você já registrou e gerencie seus modelos de prescrição, diagnóstico e conduta.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AssignmentOutlinedIcon />}
                  onClick={() => navigate(ROUTES.MY_MEDICAL_RECORDS)}
                  sx={{
                    width: { xs: "100%", sm: 260 },
                    whiteSpace: "nowrap",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  Prontuários
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {isSecretary && (
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={600} gutterBottom>
                Médicos vinculados
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Veja os médicos para os quais você pode gerenciar a agenda e os locais de atendimento.
              </Typography>

              {loadingLinkedDoctors && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Carregando médicos vinculados...
                </Typography>
              )}

              {linkedDoctorsError && (
                <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                  {linkedDoctorsError}
                </Typography>
              )}

              {!loadingLinkedDoctors && !linkedDoctorsError && linkedDoctors.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Nenhum médico vinculado a esta secretária.
                </Typography>
              )}

              <Stack spacing={2} sx={{ mt: 2 }}>
                {linkedDoctors.map((doc) => {
                  const firstLocal = doc.locals && doc.locals[0];
                  return (
                    <Card key={doc.doctorId} variant="outlined" sx={{ borderRadius: 2 }}>
                      <CardContent>
                        <Stack spacing={1.5}>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {doc.name}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {doc.email}
                          </Typography>
                          {doc.crm && (
                            <Typography variant="body2" color="text.secondary">
                              CRM: {doc.crm}
                            </Typography>
                          )}
                          {firstLocal && (
                            <Typography variant="body2" color="text.secondary">
                              Clínica: {firstLocal.localName || "(sem nome)"} - {firstLocal.city || ""}
                            </Typography>
                          )}

                          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} sx={{ mt: 1 }}>
                            <Button
                              variant="contained"
                              size="small"
                              onClick={() => navigate(ROUTES.SCHEDULE, { state: { doctorId: doc.doctorId } })}
                            >
                              Ver agenda
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<LocalHospitalOutlinedIcon />}
                              onClick={() => handleOpenClinicModal(doc)}
                            >
                              Clínica
                            </Button>
                          </Stack>
                        </Stack>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </CardContent>
          </Card>
        )}

        {canAccessReports && (
          <Card>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2}
              >
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Relatórios
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Visualize e acompanhe os principais relatórios da clínica.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<AssessmentOutlinedIcon />}
                  onClick={handleOpenReports}
                  sx={{
                    width: { xs: "100%", sm: 260 },
                    whiteSpace: "nowrap",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  Relatórios
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {canAccessFinance && (
          <Card>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2}
              >
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Financeiro
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Consulte pagamentos, totais recebidos e repasses por período.
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<PaidOutlinedIcon />}
                  onClick={handleOpenFinance}
                  sx={{
                    width: { xs: "100%", sm: 260 },
                    whiteSpace: "nowrap",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  Financeiro
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {canAccessAuditLogs && (
          <Card>
            <CardContent>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", sm: "center" }}
                spacing={2}
              >
                <Box>
                  <Typography variant="h6" fontWeight={600}>
                    Auditoria
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Consulte logs de ações (criação, atualização, exclusão, login e pagamentos).
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<FactCheckOutlinedIcon />}
                  onClick={handleOpenAuditLogs}
                  sx={{
                    width: { xs: "100%", sm: 260 },
                    whiteSpace: "nowrap",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  Ver logs
                </Button>
              </Stack>
            </CardContent>
          </Card>
        )}

        {canManageUsers && (
          <>
            <Card>
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Usuários
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Crie novos usuários do tipo padrão ou com outras funções (roles) definidas no backend.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PersonAddAlt1OutlinedIcon />}
                    onClick={() => setOpenCreateUserModal(true)}
                    sx={{
                      width: { xs: "100%", sm: 260 },
                      whiteSpace: "nowrap",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    Criar usuário
                  </Button>
                </Stack>
              </CardContent>
            </Card>

            <Card>
              <CardContent>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  justifyContent="space-between"
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  spacing={2}
                >
                  <Box>
                    <Typography variant="h6" fontWeight={600}>
                      Verificar usuários
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Liste e filtre usuários existentes, acessando rapidamente o perfil de cada um.
                    </Typography>
                  </Box>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => navigate(ROUTES.USERS_LIST)}
                    sx={{
                      width: { xs: "100%", sm: 260 },
                      whiteSpace: "nowrap",
                      justifyContent: "center",
                      gap: 1,
                    }}
                  >
                    Ver usuários
                  </Button>
                </Stack>
              </CardContent>
            </Card>
          </>
        )}
      </Box>

      <CreateUserModal
        open={openCreateUserModal}
        onClose={() => setOpenCreateUserModal(false)}
      />

      {/* Modal de clínica para médicos vinculados (SECRETARY) */}
      <Dialog open={clinicModalOpen} onClose={handleCloseClinicModal} fullWidth maxWidth="sm">
        <DialogTitle>Clínica do médico</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {clinicDoctor && (
              <>
                <Typography variant="subtitle1" fontWeight={600}>
                  {clinicDoctor.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {clinicDoctor.email}
                </Typography>
                {clinicDoctor.crm && (
                  <Typography variant="body2" color="text.secondary">
                    CRM: {clinicDoctor.crm}
                  </Typography>
                )}

                {clinicDoctor.locals && clinicDoctor.locals.length > 0 && (
                  <Stack spacing={1}>
                    <Typography variant="body2" color="text.secondary">
                      Clínicas cadastradas:
                    </Typography>
                    {clinicDoctor.locals.map((loc) => (
                      <Box
                        key={loc.id}
                        sx={{
                          borderRadius: 1,
                          border: "1px solid #e0e0e0",
                          p: 1,
                        }}
                      >
                        <Typography variant="body2" color="text.primary">
                          {loc.localName || "(sem nome)"}
                          {loc.city ? ` - ${loc.city}` : ""}
                        </Typography>
                        <Button
                          variant="text"
                          size="small"
                          sx={{ mt: 0.5 }}
                          onClick={() =>
                            setClinicLocal({
                              id: loc.id,
                              localName: loc.localName || "",
                              address: loc.address || "",
                              city: loc.city || "",
                              state: loc.state || "",
                              zip: loc.zip_code || "",
                              phone: loc.phone || "",
                            })
                          }
                        >
                          Editar esta clínica
                        </Button>
                      </Box>
                    ))}
                  </Stack>
                )}

                <Button
                  variant="text"
                  size="small"
                  onClick={() => setClinicLocal(emptyClinicLocal)}
                  sx={{ mt: 1, alignSelf: "flex-start" }}
                >
                  Adicionar nova clínica
                </Button>
              </>
            )}

            <TextField
              label="Nome da clínica / consultório"
              value={clinicLocal.localName}
              onChange={(e) => setClinicLocal((prev) => ({ ...prev, localName: e.target.value }))}
              fullWidth
            />
            <TextField
              label="Endereço"
              value={clinicLocal.address}
              onChange={(e) => setClinicLocal((prev) => ({ ...prev, address: e.target.value }))}
              fullWidth
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TextField
                label="Cidade"
                value={clinicLocal.city}
                onChange={(e) => setClinicLocal((prev) => ({ ...prev, city: e.target.value }))}
                fullWidth
              />
              <TextField
                label="Estado"
                value={clinicLocal.state}
                onChange={(e) => setClinicLocal((prev) => ({ ...prev, state: e.target.value }))}
                fullWidth
              />
              <TextField
                label="CEP"
                value={clinicLocal.zip}
                onChange={(e) => setClinicLocal((prev) => ({ ...prev, zip: e.target.value }))}
                fullWidth
              />
            </Stack>
            <TextField
              label="Telefone da clínica"
              value={clinicLocal.phone}
              onChange={(e) => setClinicLocal((prev) => ({ ...prev, phone: e.target.value }))}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseClinicModal} color="inherit" disabled={savingClinicLocal}>
            Cancelar
          </Button>
          <Button onClick={handleSaveClinicLocal} variant="contained" disabled={savingClinicLocal}>
            {savingClinicLocal ? "Salvando..." : "Salvar"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
